import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Check, Eye, MoreHorizontal, RotateCcw, Trash2 } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  adminListFeedback,
  deleteFeedback,
  markFeedbackSeen,
  setFeedbackStatus,
  type AdminFeedback,
  type FeedbackStatus,
} from '@/lib/feedback-api'
import { formatDateTime } from '@/lib/date'
import { getErrorMessage } from '@/lib/error-messages'
import { queryKeys } from '@/lib/query-keys'

type StatusFilter = FeedbackStatus | 'all'

const statusBadges: Record<FeedbackStatus, { label: string; className: string }> = {
  new: { label: 'Neu', className: 'bg-amber-500/15 text-amber-600 dark:text-amber-400' },
  done: { label: 'Erledigt', className: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' },
}

function senderName(feedback: AdminFeedback) {
  return feedback.full_name || feedback.email || 'Gelöschter Benutzer'
}

export function AdminFeedbackPage() {
  const queryClient = useQueryClient()
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('new')
  const [detailFor, setDetailFor] = useState<AdminFeedback | null>(null)
  const [deleteFor, setDeleteFor] = useState<AdminFeedback | null>(null)

  const feedbackQuery = useQuery({
    queryKey: queryKeys.adminFeedback,
    queryFn: adminListFeedback,
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey: queryKeys.adminFeedback })

  // Visiting the list counts as seeing all feedback, so the notice dialog stays quiet.
  const latestCreatedAt = feedbackQuery.data?.[0]?.created_at
  useEffect(() => {
    if (!latestCreatedAt) return
    markFeedbackSeen(latestCreatedAt)
      .then(() => queryClient.invalidateQueries({ queryKey: queryKeys.adminUnseenFeedback }))
      .catch(() => undefined)
  }, [latestCreatedAt, queryClient])

  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: FeedbackStatus }) => setFeedbackStatus(id, status),
    onSuccess: (_, { status }) => {
      invalidate()
      toast.success(status === 'done' ? 'Feedback als erledigt markiert.' : 'Feedback wieder geöffnet.')
      setDetailFor(null)
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteFeedback(id),
    onSuccess: () => {
      invalidate()
      toast.success('Feedback gelöscht.')
      setDeleteFor(null)
      setDetailFor(null)
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const allFeedback = feedbackQuery.data ?? []
  const counts = {
    new: allFeedback.filter((feedback) => feedback.status === 'new').length,
    done: allFeedback.filter((feedback) => feedback.status === 'done').length,
    all: allFeedback.length,
  }
  const feedbackList =
    statusFilter === 'all' ? allFeedback : allFeedback.filter((feedback) => feedback.status === statusFilter)

  const toggleStatus = (feedback: AdminFeedback) =>
    statusMutation.mutate({ id: feedback.id, status: feedback.status === 'new' ? 'done' : 'new' })

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold">Feedback</h2>
          <p className="text-sm text-muted-foreground">Von Teilnehmern über die App eingemeldetes Feedback.</p>
        </div>
        <Tabs value={statusFilter} onValueChange={(value) => setStatusFilter(value as StatusFilter)}>
          <TabsList>
            <TabsTrigger value="new">Offen ({counts.new})</TabsTrigger>
            <TabsTrigger value="done">Erledigt ({counts.done})</TabsTrigger>
            <TabsTrigger value="all">Alle ({counts.all})</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <Card>
        <CardContent className="p-0">
          {feedbackQuery.isLoading ? (
            <div className="space-y-2 p-6">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
            </div>
          ) : feedbackQuery.isError ? (
            <div className="flex flex-col items-center gap-3 p-8 text-center">
              <p className="text-sm text-destructive">{getErrorMessage(feedbackQuery.error)}</p>
              <Button variant="outline" size="sm" onClick={() => feedbackQuery.refetch()}>
                Erneut laden
              </Button>
            </div>
          ) : feedbackList.length === 0 ? (
            <p className="p-8 text-center text-sm text-muted-foreground">
              {statusFilter === 'new' ? 'Kein offenes Feedback vorhanden.' : 'Kein Feedback vorhanden.'}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Eingegangen</TableHead>
                  <TableHead>Teilnehmer</TableHead>
                  <TableHead>Feedback</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {feedbackList.map((feedback) => {
                  const badge = statusBadges[feedback.status]

                  return (
                    <TableRow key={feedback.id}>
                      <TableCell className="whitespace-nowrap text-sm">{formatDateTime(feedback.created_at)}</TableCell>
                      <TableCell className="max-w-[12rem]">
                        <div className="truncate text-sm font-medium">{senderName(feedback)}</div>
                        {feedback.full_name && feedback.email && (
                          <div className="truncate text-xs text-muted-foreground">{feedback.email}</div>
                        )}
                      </TableCell>
                      <TableCell className="max-w-md">
                        <button
                          type="button"
                          className="block w-full truncate text-left font-medium hover:underline"
                          onClick={() => setDetailFor(feedback)}
                        >
                          {feedback.title}
                        </button>
                        <div className="truncate text-xs text-muted-foreground">{feedback.body}</div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className={badge.className}>
                          {badge.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" aria-label="Aktionen">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onSelect={() => setDetailFor(feedback)}>
                              <Eye className="mr-2 h-4 w-4" />
                              Anzeigen
                            </DropdownMenuItem>
                            <DropdownMenuItem onSelect={() => toggleStatus(feedback)}>
                              {feedback.status === 'new' ? (
                                <>
                                  <Check className="mr-2 h-4 w-4" />
                                  Als erledigt markieren
                                </>
                              ) : (
                                <>
                                  <RotateCcw className="mr-2 h-4 w-4" />
                                  Wieder öffnen
                                </>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-destructive focus:text-destructive"
                              onSelect={() => setDeleteFor(feedback)}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Löschen
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={detailFor !== null} onOpenChange={(open) => !open && setDetailFor(null)}>
        <DialogContent className="max-w-2xl">
          {detailFor && (
            <>
              <DialogHeader>
                <DialogTitle className="pr-6">{detailFor.title}</DialogTitle>
                <DialogDescription>
                  {senderName(detailFor)}
                  {detailFor.full_name && detailFor.email ? ` · ${detailFor.email}` : ''} ·{' '}
                  {formatDateTime(detailFor.created_at)}
                </DialogDescription>
              </DialogHeader>
              <div className="flex items-center gap-2 text-sm">
                <Badge variant="secondary" className={statusBadges[detailFor.status].className}>
                  {statusBadges[detailFor.status].label}
                </Badge>
                {detailFor.resolved_at && (
                  <span className="text-muted-foreground">am {formatDateTime(detailFor.resolved_at)}</span>
                )}
              </div>
              <p className="max-h-[50vh] overflow-y-auto whitespace-pre-wrap break-words text-sm">{detailFor.body}</p>
              <DialogFooter className="gap-2">
                <Button variant="ghost" className="text-destructive" onClick={() => setDeleteFor(detailFor)}>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Löschen
                </Button>
                <Button disabled={statusMutation.isPending} onClick={() => toggleStatus(detailFor)}>
                  {detailFor.status === 'new' ? (
                    <>
                      <Check className="mr-2 h-4 w-4" />
                      Als erledigt markieren
                    </>
                  ) : (
                    <>
                      <RotateCcw className="mr-2 h-4 w-4" />
                      Wieder öffnen
                    </>
                  )}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={deleteFor !== null} onOpenChange={(open) => !open && setDeleteFor(null)}>
        <DialogContent>
          {deleteFor && (
            <>
              <DialogHeader>
                <DialogTitle>Feedback löschen?</DialogTitle>
                <DialogDescription>„{deleteFor.title}“ wird endgültig gelöscht.</DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setDeleteFor(null)}>
                  Abbrechen
                </Button>
                <Button
                  variant="destructive"
                  disabled={deleteMutation.isPending}
                  onClick={() => deleteMutation.mutate(deleteFor.id)}
                >
                  Löschen
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
